import type { UseQueryOptions, UseQueryResult } from 'react-query';
import { useQuery, useQueryClient } from 'react-query';

import type { IQueryKeyFactory } from '~/helpers/types';
import type { ApiError } from '~/lib/api';
import type {
  IApiGetDocumentsRequestBody,
  IApiGetDocumentsResponseBody,
} from '../DocumentsApi.types';
import { useApi } from '~/lib/api/useApi';
import { documentQueryKeyFactory } from './useDocumentQuery';

export type IUseDocumentsQueryValues = {
  appId: string;
  body: IApiGetDocumentsRequestBody;
};

export const documentsQueryKeyFactory: IQueryKeyFactory<
  IUseDocumentsQueryValues
> = (values) =>
  values &&
  (values.body
    ? (['documents', values.appId, values.body] as const)
    : (['documents', values.appId] as const));

export const useDocumentsQuery = (
  values?: IUseDocumentsQueryValues,
  options?: UseQueryOptions<IApiGetDocumentsResponseBody, ApiError>,
): UseQueryResult<IApiGetDocumentsResponseBody, ApiError> => {
  const api = useApi();
  const queryClient = useQueryClient();

  return useQuery({
    queryKey: documentsQueryKeyFactory(values),
    queryFn: values
      ? () => api.documents.getAll(values.appId, values.body)
      : undefined,
    onSuccess: (documents) => {
      documents.list?.forEach((document) => {
        const queryKey = documentQueryKeyFactory({
          appId: document.appId,
          documentId: document._id,
        });
        if (queryKey) {
          queryClient.setQueryData(queryKey, document);
        }
      });
      options?.onSuccess?.(documents);
    },
    ...options,
  });
};
